import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  CompanySelect,
  Documents,
  EditPage,
  FuseInput,
  FuseSelect,
  InvestorParticipations,
  Notes,
} from '/src/components';
import { useFilterStore, useLoanStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Loan, Note } from '/src/lib/models';
import { CRUDMode, DataModelType, FormErrors, LoanType, PaymentMethod } from '/src/lib/types';
import { isNumber } from '/src/utils';

/**
 * Edit Loan Page
 */
export const EditLoanPage = observer(() => {
  /** Context **/
  const loanStore = useLoanStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<Loan>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState([
    true, // Participations
    false, // Company Comments
    false, // Socket Comments
    false, // Documents
  ]);

  /** Computed **/
  const dealType = useMemo(() => (formValues?.isOwned ? LoanType.Owned : LoanType.Loan), [formValues?.isOwned]);
  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const errorMessages = {
      name: 'Deal Name is required',
      dealType: 'Deal Type is required',
      borrower: 'Borrower is required',
      amount: 'Amount is required',
      advanceRate: 'Advance Rate is required',
      term: 'Term is required',
      maturityDate: 'Maturity Date is required',
      lastAdvanceDate: 'Last Advance Date is required',
      interestRate: 'Interest Rate is required',
      originationFee: 'Origination Fee is required',
      minimumPayment: 'Minimum Payment is required',
      payDownPercent: 'Pay Down Percent is required',
      minPmtMethod: 'Minimum Payment Method is required',
    };

    if (!formValues?.['name']) {
      errors['name'] = errorMessages['name'];
    }
    if (typeof formValues?.['isOwned'] !== 'boolean') {
      errors['dealType'] = errorMessages['dealType'];
    }
    if (!formValues?.['borrower']) {
      errors['borrower'] = errorMessages['borrower'];
    }
    if (!formValues?.['amount'] || !isNumber(formValues?.['amount'])) {
      errors['amount'] = errorMessages['amount'];
    }
    if (!isNumber(formValues?.['advanceRate'])) {
      errors['advanceRate'] = errorMessages['advanceRate'];
    }

    if (dealType === LoanType.Loan) {
      if (!formValues?.['maturityDate']) {
        errors['maturityDate'] = errorMessages['maturityDate'];
      }
      if (!formValues?.['lastAdvanceDate']) {
        errors['lastAdvanceDate'] = errorMessages['lastAdvanceDate'];
      }
      if (!formValues?.['term']) {
        errors['term'] = errorMessages['term'];
      }
      if (!isNumber(formValues?.['interestRate'])) {
        errors['interestRate'] = errorMessages['interestRate'];
      }
      if (!isNumber(formValues?.['originationFee'])) {
        errors['originationFee'] = errorMessages['originationFee'];
      }
      if (formValues?.['minPmtMethod'] === PaymentMethod.SetAmount && !formValues?.['minimumPayment']) {
        errors['minimumPayment'] = errorMessages['minimumPayment'];
      }
      if (
        formValues?.['minPmtMethod'] === PaymentMethod.PayDown &&
        dealType === LoanType.Loan &&
        !isNumber(formValues?.['payDownPercent'])
      ) {
        errors['payDownPercent'] = errorMessages['payDownPercent'];
      }
      if (!formValues?.['minPmtMethod']) {
        errors['minPmtMethod'] = errorMessages['minPmtMethod'];
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the Loan.
   *
   * @remarks
   * Redirects to the Loan's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await loanStore.update(formValues);
      if (!response) throw new Error(`Could not update Loan with ID "${formValues._id}"`);

      toast.success(toastMessages.updateLoan.success);
      navigate(`/deals/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updateLoan.error);
    }
  };

  /**
   * Updates the Company Comments list.
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateCompanyComments = async (companyComments: Note[]) => {
    if (!formValues) return;

    const response = await loanStore.update({ ...formValues, companyComments });
    if (!response) throw new Error(`Error updating company comments for Loan ID ${formValues._id}`);

    loanStore.currentLoan = await loanStore.get(formValues._id);
  };

  /**
   * Update Socket Comments
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateSocketComments = async (socketComments: Note[]) => {
    if (!formValues) return;

    const response = await loanStore.update({ ...formValues, socketComments });
    if (!response) throw new Error(`Error updating socket comments for Loan ID ${formValues._id}`);

    loanStore.currentLoan = await loanStore.get(formValues._id);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!formValues) return;

    await loanStore.update({ ...formValues, files });

    updateFormValue('files')(files);
  };

  /** Effects **/

  useEffect(() => {
    if (!params.loanId) return;

    Promise.all([loanStore.get(params.loanId)]);
  }, [params.loanId]);

  useEffect(() => {
    if (loanStore.currentLoan) {
      setFormValues(loanStore.currentLoan);
    }
  }, [loanStore.currentLoan]);

  useEffect(() => {
    if (formValues?.minPmtMethod) {
      updateFormValue('minimumPayment')(0);
      updateFormValue('payDownPercent')(0);
    }
  }, [formValues?.minPmtMethod]);

  /** Render **/

  return (
    <EditPage
      modelName={loanStore.currentLoan?.name ?? ''}
      modelType={DataModelType.Loan}
      editVerb="Edit"
      onEdit={update}
      sidebarFlex="40"
      contentFlex="60"
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      sidebar={
        <>
          <InvestorParticipations
            open={sidebarItems[0]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(0, !sidebarItems[0]))}
            participations={formValues?.lenders ?? []}
            setParticipations={updateFormValue('lenders')}
          />
          <Notes
            mode={CRUDMode.View}
            notes={formValues?.companyComments ?? []}
            title="Notes"
            tooltip="Notes"
            sidebarIndex={1}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateNotes={updateCompanyComments}
          />
          {userStore.isAdmin && (
            <Notes
              mode={CRUDMode.View}
              notes={formValues?.socketComments ?? []}
              title="Internal Notes"
              tooltip="Internal Notes"
              sidebarIndex={2}
              sidebarItems={sidebarItems}
              setSidebarItems={setSidebarItems}
              updateNotes={updateSocketComments}
            />
          )}
          <Documents
            mode={CRUDMode.Edit}
            documents={formValues?.files ?? []}
            sidebarIndex={3}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateDocuments={updateFiles}
          />
        </>
      }
    >
      {/* Borrower */}
      <CompanySelect
        id="company"
        value={formValues?.borrower?._id ?? ''}
        setValue={updateFormValue('borrower')}
        companies={companies}
        disabled
        error={formErrors.borrower}
      />

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Deal Name */}

        <Box display="flex" flex={70}>
          <FuseInput
            label="Deal Name"
            value={formValues?.name}
            updateValue={updateFormValue('name')}
            required
            error={formErrors.name}
          />
        </Box>

        {/* Deal Type */}
        <Box display="flex" flex={30}>
          <Tooltip title="Deal type cannot be changed after the Deal is created">
            <Box>
              <FuseSelect
                label="Deal Type"
                options={Object.values(LoanType)}
                value={formValues?.isOwned ? LoanType.Owned : LoanType.Loan}
                updateValue={(loanType) => updateFormValue('isOwned')(loanType === LoanType.Owned)}
                placeholder="Deal Type"
                required
                disabled
                flex="30"
                error={formErrors.dealType}
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Amount */}
        <FuseInput
          label="Amount"
          value={formValues?.amount}
          tooltip={'Total indebtedness including principal and outstanding interest charges ("Commitment Amount")'}
          updateValue={updateFormValue('amount')}
          min="0"
          moneyMask
          required
          error={formErrors.amount}
        />
        {/* Advance Rate */}
        <FuseInput
          label="Advance Rate"
          value={formValues?.advanceRate}
          tooltip="Percent of the value of eligible assets that can be made available for advances"
          updateValue={updateFormValue('advanceRate')}
          min="0"
          percentageMask
          required
          error={formErrors.advanceRate}
        />
      </Box>

      {dealType === LoanType.Loan && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Minimum Payment Method */}
          <FuseSelect
            label="Min. Monthly Pmt. Method"
            value={formValues?.minPmtMethod}
            options={Object.values(PaymentMethod)}
            updateValue={updateFormValue('minPmtMethod')}
            placeholder="Select a Method for Calculating Minimum Monthly Payments"
            flex="30"
            error={formErrors.minPmtMethod}
          />

          {/* Minimum Payment */}
          {formValues?.minPmtMethod === PaymentMethod.SetAmount && (
            <FuseInput
              label="Minimum Payment"
              value={formValues?.minimumPayment}
              updateValue={updateFormValue('minimumPayment')}
              min="0"
              moneyMask
              error={formErrors.minimumPayment}
            />
          )}

          {/* Pay Down Percent */}
          {formValues?.minPmtMethod === PaymentMethod.PayDown && (
            <FuseInput
              label="Pay Down Percent"
              value={formValues?.payDownPercent}
              updateValue={updateFormValue('payDownPercent')}
              min="0"
              percentageMask
              error={formErrors.payDownPercent}
            />
          )}

          {/* Term */}
          <FuseInput
            label="Term (Months)"
            value={formValues?.term}
            updateValue={updateFormValue('term')}
            type="number"
            min="0"
            required={dealType === LoanType.Loan}
            error={formErrors.term}
          />
        </Box>
      )}

      {dealType === LoanType.Loan && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Maturity Date */}
          <FuseInput
            label="Maturity Date"
            value={formValues?.maturityDate}
            updateValue={updateFormValue('maturityDate')}
            type="date"
            required
            error={formErrors.maturityDate}
          />

          {/* Last Advance Date */}
          <FuseInput
            label="Last Advance Date"
            value={formValues?.lastAdvanceDate}
            updateValue={updateFormValue('lastAdvanceDate')}
            type="date"
            required
            error={formErrors.lastAdvanceDate}
          />
        </Box>
      )}

      {dealType === LoanType.Loan && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Origination Fee */}
          <FuseInput
            label="Origination Fee"
            value={formValues?.originationFee}
            tooltip="Percentage fee charged for each advance"
            updateValue={updateFormValue('originationFee')}
            min="0"
            percentageMask
            required
            error={formErrors.originationFee}
          />

          {/* Monthly Interest Rate */}
          <FuseInput
            label="Monthly Interest Rate"
            value={formValues?.interestRate}
            tooltip="Applied daily to the outstanding principal balance"
            updateValue={updateFormValue('interestRate')}
            min="0"
            percentageMask
            required
            error={formErrors.interestRate}
          />
        </Box>
      )}

      {dealType === LoanType.Owned && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Administrative Fee % */}
          <FuseInput
            label="Administrative Fee"
            value={formValues?.adminFee}
            updateValue={updateFormValue('adminFee')}
            min="0"
            percentageMask
            required
            error={formErrors.adminFee}
          />

          {/* Discount Fee % */}
          <FuseInput
            label="Discount Fee"
            value={formValues?.discountFee}
            updateValue={updateFormValue('discountFee')}
            min="0"
            percentageMask
            required
            error={formErrors.discountFee}
          />
        </Box>
      )}
    </EditPage>
  );
});
